import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CampaignBillingSummary from '../components/campaign-billing/summary';
import UpdateCampaignBilling from '../components/campaign-billing/updateCampaignBilling';
import SuperAdminPanel from '../components/settings/superAdminPanel';
import CampaignBillingAudit from '../components/campaign-billing/audit';
import FilterAdminPanel from '../components/settings/filterAdminPanel';
import TabButton from '../components/benefits/tabButton';
import useAuth from '../utils/auth';
import Role from '../types/enums';
import OnboardingPanel from '../components/settings/onboardingPanel';
import OrganisationManagerPanel from '../components/settings/organisationManagerPanel';

const OverviewTab = 'Overview';
const FilterAdminTab = 'Filter Admin';
const OnboardingTab = 'Onboarding';
const ManageOrganisationsTab = 'Organisations';
const TabSearchParameter = 'tab';

export default function Admin() {
  const authUser = useAuth();
  const isSuperAdmin = authUser.role === Role.SuperAdmin;
  const tabs = isSuperAdmin
    ? [OverviewTab, OnboardingTab, ManageOrganisationsTab, FilterAdminTab]
    : [OverviewTab];

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(TabSearchParameter);
  const activeTab = tabFromUrl && tabs.includes(tabFromUrl) ? tabFromUrl : OverviewTab;
  const setActiveTab = (tab: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === OverviewTab) {
          next.delete(TabSearchParameter);
        } else {
          next.set(TabSearchParameter, tab);
        }
        return next;
      },
      { replace: true },
    );
  };

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedOrganisationName, setSelectedOrganisationName] = useState<string | null>(null);

  const handleBillingUpdated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleOrganisationSelect = (organisationName: string) => {
    setSelectedOrganisationName(organisationName);
  };

  return (
    <div className="flex flex-col w-full p-4 overflow-auto">
      <TabButton theme={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === OverviewTab && (
        <>
          <div className="flex flex-col mt-4">
            <h1 className="m-2 font-medium text-primary-dark">Change Organisation</h1>
            <SuperAdminPanel />
          </div>

          <div className="flex flex-col mt-8">
            <h1 className="m-2 font-medium text-primary-dark">Campaign Billing Management</h1>
            <UpdateCampaignBilling onBillingUpdated={handleBillingUpdated} />
            <div className="flex flex-row gap-4 mt-4">
              <div className="w-1/2">
                <CampaignBillingSummary
                  refreshTrigger={refreshTrigger}
                  onOrganisationSelect={handleOrganisationSelect}
                  selectedOrganisationName={selectedOrganisationName}
                />
              </div>
              <div className="w-1/2">
                {selectedOrganisationName && (
                  <CampaignBillingAudit
                    refreshTrigger={refreshTrigger}
                    organisationDisplayName={selectedOrganisationName}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === OnboardingTab && isSuperAdmin && (
      <div className="flex flex-col mt-4">
        <h1 className="m-2 font-medium text-primary-dark">Onboard organisation</h1>
        <OnboardingPanel />
      </div>
      )}

      {activeTab === ManageOrganisationsTab && isSuperAdmin && (
        <div className="flex flex-col mt-4 flex-1 min-h-0">
          <h1 className="m-2 font-medium text-primary-dark">Manage organisation</h1>
          <OrganisationManagerPanel />
        </div>
      )}

      {activeTab === FilterAdminTab && isSuperAdmin && (
        <div className="flex flex-col mt-4">
          <h1 className="m-2 font-medium text-primary-dark">Filter Admin</h1>
          <FilterAdminPanel />
        </div>
      )}
    </div>
  );
}
